import {
  Box,
  Heading,
  Image,
  VStack,
  HStack,
  Input,
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
  Flex,
  useToast,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Text,
  Spinner,
  Checkbox,
} from "@chakra-ui/react";

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CodesetSelect from "./CodesetSelect";
import UuidPreview from "./UuidPreview";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlineSave,
} from "react-icons/hi";
import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import ReferencesEditor from "./ReferencesEditor";
import ClassificationsEditor from "./ClassificationsEditor";
import AmountsEditor from "./AmountsEditor";
import DateTimesEditor from "./DateTimesEditor";
import LocationsEditor from "./LocationsEditor";
import AddressesEditor from "./AddressesEditor";
import TextEditor from "./TextEditor";
import PartiesEditor from "./PartiesEditor";
import { useAuth } from "./AuthContext";
import { canEditRecord, withOwnerOrganisation } from "./utils/ownership";
import { errorFromResponse } from "./utils/apiErrors";

const RegisterEntriesEditor = ({ entry, isNew }) => {
  const [entryData, setEntryData] = useState({});
  const [registerName, setRegisterName] = useState("");
  const [registerLoading, setRegisterLoading] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const isReadOnly = !isNew && !canEditRecord(user, entry);

  useEffect(() => {
    if (entry) {
      setEntryData({ ...entry });
    }
  }, [entry]);

  // Fetch parent register name
  useEffect(() => {
    const fetchRegisterName = async () => {
      if (!entry?.registerId) return;

      setRegisterLoading(true);
      try {
        const response = await fetch(
          `https://registers.poc.mrc.ai/api/registers/${entry.registerId}`
        );
        if (response.ok) {
          const register = await response.json();
          setRegisterName(register.name || "Unknown Register");
        }
      } catch (error) {
        console.error("Error fetching register name:", error);
      } finally {
        setRegisterLoading(false);
      }
    };

    fetchRegisterName();
  }, [entry?.registerId]);

  const handleChange = (field, value) => {
    setEntryData((prev) => {
      if (prev[field] === value) return prev;
      return { ...prev, [field]: value };
    });
  };

  const handleDirectChange = (field, value) => {
    setEntryData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // Validate registerId
    if (!entryData.registerId) {
      toast({
        title: "Validation Error",
        description: "Register ID is required. Cannot save entry without a parent register.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      console.log("Saving register entry with data:", JSON.stringify(entryData, null, 2));
      const response = await fetch(
        isNew
          ? "https://registers.poc.mrc.ai/api/registerentries"
          : `https://registers.poc.mrc.ai/api/registerentries/${entryData._id}`,
        {
          method: isNew ? "POST" : "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(isNew ? withOwnerOrganisation(entryData, user) : entryData),
        }
      );

      if (!response.ok) {
        throw await errorFromResponse(
          response,
          `Failed to ${isNew ? "create" : "save"}. Status: ${response.status}`
        );
      }

      const result = await response.json();

      toast({
        title: isNew ? "Register Entry created" : "Register Entry saved",
        description: isNew
          ? "New register entry was added."
          : "Changes saved successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      if (isNew && result._id) {
        navigate(`/registers/${entryData.registerId}/entry/${result._id}`);
      }
    } catch (error) {
      console.error("❌ Error saving register entry:", error);

      toast({
        title: "Save failed",
        description:
          error.message || "An error occurred while saving the register entry.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleClose = () => {
    navigate(`/registers/${entryData.registerId}`);
  };

  // Keyboard shortcut: Ctrl/Cmd+S to save
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSave();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [entryData]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      <Navbar />
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        <Box position="relative" zIndex={1} p={10}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={2}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <Flex mb={2}>
              <HStack spacing={6}>
                <Tooltip
                  label="Back to Register"
                  aria-label="Back To Register tooltip">
                  <IconButton
                    icon={<HiOutlineArrowNarrowLeft />}
                    size="md"
                    onClick={handleClose}
                  />
                </Tooltip>
                <VStack align="flex-start" spacing={0}>
                  <Heading size="lg">Edit Register Entry</Heading>
                  {registerLoading ? (
                    <Spinner size="xs" />
                  ) : registerName ? (
                    <Text fontSize="sm" color="gray.600">
                      Register: {registerName}
                    </Text>
                  ) : null}
                </VStack>
                <Tooltip
                  label="Save Register Entry (Ctrl/Cmd+S)"
                  aria-label="Save Register Entry Tooltip">
                  <IconButton
                    icon={<HiOutlineSave />}
                    size="md"
                    onClick={handleSave}
                    isDisabled={isReadOnly}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          </Box>

          <VStack spacing={6} align="stretch">
            <Tabs orientation="horizontal" variant="line" colorScheme="linkedin">
            <TabList>
              <Tab>Header</Tab>
              <Tab>Parties</Tab>
              <Tab>Addresses</Tab>
              <Tab>References</Tab>
              <Tab>Classifications</Tab>
              <Tab>Amounts</Tab>
              <Tab>DateTimes</Tab>
              <Tab>Text</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <UuidPreview uuid={entryData._id} />

                  <FormControl mt={4}>
                    <FormLabel>Name</FormLabel>
                    <Input
                      value={entryData.name || ""}
                      onChange={(e) => handleChange("name", e.target.value)}
                    />
                  </FormControl>

                  <FormControl mt={4}>
                    <Checkbox
                      isChecked={entryData.isDefinition || false}
                      onChange={(e) =>
                        setEntryData((prev) => ({
                          ...prev,
                          isDefinition: e.target.checked,
                        }))
                      }
                    >
                      Is Definition
                    </Checkbox>
                  </FormControl>

                  <CodesetSelect
                    label="Type"
                    codesetCode="B58"
                    value={
                      entryData.type
                        ? { code: entryData.type, description: "" }
                        : null
                    }
                    onChange={(val) => handleChange("type", val?.code)}
                  />

                  <FormControl mt={4}>
                    <FormLabel>Description</FormLabel>
                    <Input
                      value={entryData.description || ""}
                      onChange={(e) =>
                        handleChange("description", e.target.value)
                      }
                    />
                  </FormControl>

                  <FormControl mt={4}>
                    <FormLabel>Class Code</FormLabel>
                    <Input
                      value={entryData.classCode || ""}
                      onChange={(e) =>
                        handleChange("classCode", e.target.value)
                      }
                    />
                  </FormControl>

                  <FormControl mt={4}>
                    <FormLabel>Quantity</FormLabel>
                    <Input
                      type="number"
                      value={entryData.quantity || 0}
                      onChange={(e) =>
                        handleChange("quantity", parseFloat(e.target.value) || 0)
                      }
                    />
                  </FormControl>

                  <FormControl mt={4}>
                    <FormLabel>Parent ID</FormLabel>
                    <Input
                      value={entryData.parentId || ""}
                      onChange={(e) =>
                        handleChange("parentId", e.target.value)
                      }
                    />
                  </FormControl>

                  <FormControl mt={4}>
                    <FormLabel>Parent Type</FormLabel>
                    <Input
                      value={entryData.parentType || ""}
                      onChange={(e) =>
                        handleChange("parentType", e.target.value)
                      }
                    />
                  </FormControl>

                  <FormControl mt={4}>
                    <FormLabel>Parent Class Code</FormLabel>
                    <Input
                      value={entryData.parentClassCode || ""}
                      onChange={(e) =>
                        handleChange("parentClassCode", e.target.value)
                      }
                    />
                  </FormControl>

                  <FormControl mt={4}>
                    <FormLabel>Metadata Standard Code</FormLabel>
                    <Input
                      value={entryData.metadataStandardCode || ""}
                      onChange={(e) =>
                        handleChange("metadataStandardCode", e.target.value)
                      }
                    />
                  </FormControl>
                </Box>
              </TabPanel>

              <TabPanel>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <PartiesEditor
                    size="md"
                    parties={entryData.parties || []}
                    onChange={(parties) => handleDirectChange("parties", parties)}
                  />
                </Box>
              </TabPanel>

              <TabPanel>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <AddressesEditor
                    size="md"
                    addresses={entryData.addresses || []}
                    onChange={(addresses) => handleDirectChange("addresses", addresses)}
                  />
                </Box>
              </TabPanel>

              <TabPanel>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <ReferencesEditor
                    size="md"
                    references={entryData.references || []}
                    onChange={(refs) => handleDirectChange("references", refs)}
                  />
                </Box>
              </TabPanel>

              <TabPanel>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <ClassificationsEditor
                    size="md"
                    classifications={entryData.classifications || []}
                    onChange={(classifications) =>
                      handleDirectChange("classifications", classifications)
                    }
                  />
                </Box>
              </TabPanel>

              <TabPanel>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <AmountsEditor
                    size="md"
                    amounts={entryData.amounts || []}
                    onChange={(amounts) => handleDirectChange("amounts", amounts)}
                  />
                </Box>
              </TabPanel>

              <TabPanel>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <DateTimesEditor
                    size="md"
                    dateTimes={entryData.dateTimes || []}
                    onChange={(dateTimes) =>
                      handleDirectChange("dateTimes", dateTimes)
                    }
                  />
                </Box>
              </TabPanel>

              <TabPanel>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <TextEditor
                    size="md"
                    text={entryData.text || []}
                    onChange={(text) => handleDirectChange("text", text)}
                  />
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};

export default RegisterEntriesEditor;
